
import isEqual from 'lodash/isEqual';

import { FormattedMessage, IntlShape } from 'react-intl';
import { LabeledValue } from 'antd/lib/select';
import {
  CondoLocationsEnum,
  GetawaysCondoSubCategoryEnum,
  GetawaysLocationEnum,
  IAccount,
  IBudgetRange,
  ICounterItem,
  IRange,
} from '@share/common-types';
import { CondoRequestTypeEnum } from '@share/common-types';
import {
  CarsFiltersInitialState,
  filtersB2CHotelsOnlyInitialState,
  filtersB2CInitialState,
  filtersB2CVacationsInitialFilteredState,
  filtersB2CVacationsInitialState,
  filtersInitialState,
  filtersInitialVacationsFilteredState,
  filtersInitialVacationsState,
  ICarsFiltersState,
  IFiltersState,
  VICINITY,
  filtersAllInclusiveInitialState,
  filtersSundanceInitialState,
  filtersRecommnededInitialState,
  filtersRecommnededCustomerAgentInitialState,
  filtersActions,
  carsFiltersActions,
  HotelsFilterType,
} from '@share/store/slices';
import { ICondosState } from '@share/store/slices';
import { TOP_AMENITIES } from '@constants';
import { HomesTypes, SortTypes } from '@share/constants';
import {
  CHOICE_LIFESTYLE,
  CHOICE_LIFESTYLE_DESC,
  UNLIMITED_LIFESTYLE,
  UNLIMITED_LIFESTYLE_DESC,
  VALUE_LIFESTYLE,
  VALUE_LIFESTYLE_DESC,
} from './wallet';
import { IGateway } from '@store/slices';
import { getCurrencyText, isAllInclusiveFromQuery } from '@share/utils';
import { Currency } from '@components';
import { Dispatch } from 'react';
import { isEmpty } from 'lodash';

export const getAmenities = (allAmenities: ICounterItem[]): ICounterItem[] => {
  const res: ICounterItem[] = [];

  TOP_AMENITIES.forEach((amenity: string) => {
    const item = allAmenities.find(({ key }) => key.toLowerCase() === amenity.toLowerCase());

    if (item) {
      res.push(item);
    }
  });

  allAmenities.forEach((amenity: ICounterItem) => {
    if (res.every((item: ICounterItem) => item.key.toLowerCase() !== amenity.key.toLowerCase())) {
      res.push(amenity);
    }
  });

  return res;
};

export const isFiltersChanged = (
  prevFilters: IFiltersState,
  currentFilters: IFiltersState,
): boolean => {
  return !isEqual(prevFilters, currentFilters);
};

export const isCarsFiltersChanged = (
  prevFilters: ICarsFiltersState,
  currentFilters: ICarsFiltersState,
): boolean => {
  return !isEqual(prevFilters, currentFilters);
};

export const getBudgetLabelText = (range: string, currency: string, intl: IntlShape) => {
  const rangeArray = range.split('-');
  const from = rangeArray[0] !== 'undefined' ? rangeArray[0] : null;
  const to = rangeArray[1] !== 'undefined' ? rangeArray[1] : null;

  const currencyText = getCurrencyText(currency);

  if (from && to) {
    return `${currencyText}${from} ${intl.formatMessage({ id: 'to' })} ${currencyText}${to}`;
  }

  if (!from && to) {
    return `${intl.formatMessage({ id: 'less.than' })} ${currencyText}${to}`;
  }

  if (from && !to) {
    return `${intl.formatMessage({ id: 'greater.than' })} ${currencyText}${from}`;
  }

  return null;
};

export const getBudgetLabelString = (range: string, currency: string) => {
  const rangeArray = range.split('-');
  const from = rangeArray[0];
  const to = rangeArray[1];

  return getBudgetLabel({ from: +from, to: +to }, currency);
};

export const getBudgetLabel = (range: IRange, currency: string) => {
  const { from, to } = range;

  if (from && to) {
    return (
      <span>
        <Currency currency={currency} />{from} <FormattedMessage id="to" /> <Currency currency={currency} />{to}
      </span>
    );
  }

  if (!from && to) {
    return (
      <span>
        <FormattedMessage id="less.than" /> <Currency currency={currency} />{to}
      </span>
    );
  }

  if (from && !to) {
    return (
      <span>
        <FormattedMessage id="greater.than" /> <Currency currency={currency} />{from}
      </span>
    );
  }
};

export const excludeMatchingFields = (sourceObject: any, comparisonObject: any, excludedKeys: string[] = []) => {
  const newObject: any = {};
  for (const key in sourceObject) {
    // Check if the key exists in both objects and if their values are different
    if (excludedKeys.includes(key) || (
      Object.prototype.hasOwnProperty.call(comparisonObject, key) &&
      sourceObject[key] === comparisonObject[key])
    ) {
      // If values are the same, skip this field (exclude it)
      continue;
    }
    // Otherwise, add the field to the new object
    newObject[key] = sourceObject[key];
  }
  return newObject;
};

export const getMatchingFields = (sourceObject: any, comparisonObject: any, excludedKeys: string[] = []) => {
  const newObject: any = {};
  for (const key in sourceObject) {
    // Include only keys that exist in both objects, have the same value, and are not in excludedKeys
    if (
      !excludedKeys.includes(key) &&
      Object.prototype.hasOwnProperty.call(comparisonObject, key) &&
      sourceObject[key] === comparisonObject[key]
    ) {
      newObject[key] = sourceObject[key];
    }
  }
  return newObject;
};

export const getInitialHotelFiltersState = (account: IAccount, isVacationRentals: boolean | null | undefined) => {
  const isB2C = account?.isB2C;
  const walletWalletSavings = account?.walletWalletSavings;
  const forceDisableVacationRentals = account?.forceDisableVacationRentals;
  const restrictedVacationRentals = account?.restrictedVacationRentals;
  const restrictedVacationRentalsList = account?.restrictedVacationRentalsList;
  const isSaverRuleProfits = account?.isSaverRuleProfits;
  const isCustomerAgent = account?.isCustomerAgent;
  const isSundance = account?.isSundance;
  const isQuickEnabled = account?.hasQuick;
  
  let initialState = isSundance? 
                          filtersSundanceInitialState : 
                          isSaverRuleProfits ?
                              filtersRecommnededInitialState :
                              isCustomerAgent ?
                                  filtersRecommnededCustomerAgentInitialState :
                                  (!isB2C || walletWalletSavings) ? 
                                      isVacationRentals ?
                                          restrictedVacationRentals ?
                                              restrictedVacationRentalsList?.length ? { ...filtersInitialVacationsFilteredState, accommodationType: [...restrictedVacationRentalsList] } : filtersInitialVacationsFilteredState : 
                                              filtersInitialVacationsState :
                                          filtersInitialState : 
                                      forceDisableVacationRentals ? 
                                          filtersB2CHotelsOnlyInitialState : 
                                          isVacationRentals ?
                                              restrictedVacationRentals ?
                                                  restrictedVacationRentalsList?.length ? { ...filtersB2CVacationsInitialFilteredState, accommodationType: [...restrictedVacationRentalsList] } : filtersB2CVacationsInitialFilteredState : 
                                                  filtersB2CVacationsInitialState :
                                              filtersB2CInitialState;

  if (isQuickEnabled) {
    initialState = { ...initialState, sortBy: SortTypes.MostBooked };
  }
  
  return initialState;
};

export const getHotelsFiltersType = (account: IAccount, isVacationRentals: boolean | null | undefined): HotelsFilterType => {
  const isAllInclusive = isAllInclusiveFromQuery();
  const isSundace = account?.isSundance;
  const isSaverRuleProfits = account?.isSaverRuleProfits;
  const isCustomerAgent = account?.isCustomerAgent;
  const isB2C = account?.isB2C;
  const walletWalletSavings = account?.walletWalletSavings;
  const forceDisableVacationRentals = account?.forceDisableVacationRentals;
  const restrictedVacationRentals = account?.restrictedVacationRentals;

  return isAllInclusive ?
              HotelsFilterType.AllInclusive :
              isSundace ? 
                  HotelsFilterType.Sundance : 
                  isSaverRuleProfits ?
                      HotelsFilterType.Recommended :
                      isCustomerAgent ?
                          HotelsFilterType.RecommendedCustomerAgent  :
                          (!isB2C || walletWalletSavings) ? 
                              isVacationRentals ?
                                  restrictedVacationRentals ?
                                      HotelsFilterType.NormalVacationsFiltered :
                                      HotelsFilterType.NormalVacations :
                                  HotelsFilterType.Normal : 
                                      forceDisableVacationRentals ? 
                                          HotelsFilterType.B2cHotelOnly : 
                                          isVacationRentals ?
                                              restrictedVacationRentals ?
                                                  HotelsFilterType.B2CVacationsFiltered :
                                                  HotelsFilterType.B2CVacations :
                                              HotelsFilterType.B2C;
};

export const isFiltersEmpty = (currentFilters: IFiltersState, account: IAccount, isVacationRentals: boolean | null | undefined): boolean => {
  const isAllInclusive = isAllInclusiveFromQuery();
  const fieldsToRemove = { selectedAutocompleteLabel: undefined, autocompleteItems: undefined, autocompleteLoading: undefined };
  const currentFiltersModified = currentFilters ? { ...currentFilters, ...fieldsToRemove } : currentFilters;
  if (isAllInclusive) {
    const initialStateModified = { ...filtersAllInclusiveInitialState, ...fieldsToRemove };
    return isEqual(currentFiltersModified, initialStateModified);
  }
  
  const initialState = getInitialHotelFiltersState(account, isVacationRentals);
  const initialStateModified = { ...initialState, ...fieldsToRemove };

  return isEqual(currentFiltersModified, initialStateModified);
};

export const getHotelsFiltersNotEmptyFields = (currentFilters: IFiltersState, account: IAccount, isVacationRentals: boolean | null | undefined): string[] => {
  const isAllInclusive = isAllInclusiveFromQuery();
  const fieldsToRemove = { selectedAutocompleteLabel: undefined, autocompleteItems: undefined, autocompleteLoading: undefined };
  const currentFiltersModified = currentFilters ? { ...currentFilters, ...fieldsToRemove } : currentFilters;
  const initialStateModified = isAllInclusive ? { ...filtersAllInclusiveInitialState, ...fieldsToRemove } : { ...getInitialHotelFiltersState(account, isVacationRentals), ...fieldsToRemove };
  return Object.keys(getMatchingFields(currentFiltersModified, initialStateModified, Object.keys(fieldsToRemove)));
};

export const isCarsFiltersEmpty = (currentFilters: ICarsFiltersState): boolean => {
  return isEqual(
    getCarsFilterStateExcluded(currentFilters),
    getCarsFilterStateExcluded(CarsFiltersInitialState),
  );
};

const getCarsFilterStateExcluded = (filters: ICarsFiltersState) => {
  const {
    simpleTypeQuick,
    rateDistanceQuick,
    rentalCompanyQuick,
    fuelQuick,
    ...filtersExluced
  } = filters;
  return filtersExluced;
};

const zero = 0;
const maxFilterCount = 6;

const getCounterKey = (counter: ICounterItem | IBudgetRange): string => {
  const key = counter?.key;
  if (typeof key === 'string') {
    return key;
  } else {
    const rangeKey = key as IRange;

    if (!rangeKey?.from && !rangeKey?.to) {
      return ``;
    }

    return `${rangeKey.from}-${rangeKey.to}`;
  }
};

export const getFilterListB = (
  counters: (ICounterItem | IBudgetRange)[],
  selected: string[],
  displayFull: boolean,
  exluceNonSelected?: boolean,
) => {
  let list = counters;
  if (counters?.length && selected?.length) {
    const countersToMoveTop = counters
      .filter((a) => {
        return selected.includes(getCounterKey(a));
      })
      .map((c) => ({ ...c }));
    if (exluceNonSelected) {
      list = [...countersToMoveTop];
    } else {
      const countersToKeep = counters
        .filter((a) => !selected.includes(getCounterKey(a)))
        .map((c) => ({ ...c }));
      list = [...countersToMoveTop, ...countersToKeep];
    }
  }

  return displayFull ? list : list.slice(zero, maxFilterCount);
};

export const getFilterList = (
  counters: ICounterItem[],
  selected: string[],
  displayFull: boolean,
) => {
  let list = counters;
  if (counters?.length && selected?.length) {
    const countersToMoveTop = counters.filter((a) => selected.includes(a.key));
    const countersToKeep = counters.filter((a) => !selected.includes(a.key));
    list = [...countersToMoveTop, ...countersToKeep];
  }

  return displayFull ? list : list.slice(zero, maxFilterCount);
};

export const getGetawaySortOptions = (): LabeledValue[] => {
  return [
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.low.to.price" /> },
    { value: SortTypes.PriceDesc, label: <FormattedMessage id="sort.high.to.price" /> },
  ];
};

export const getAllInclusiveSortOptions = (): LabeledValue[] => {
  return [
    { value: SortTypes.BiggestSavings, label: <FormattedMessage id="sort.biggest" /> },
    { value: SortTypes.BiggestSavingsPercentage, label: <FormattedMessage id="sort.biggest.percent" /> },
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.low.to.price" /> }
  ];
};

export const getGetawayOptionLabel = (location: any): string => {
  const countryIncludesVicinity = location.country?.includes(VICINITY);
  const cityIncludesVicinity = location.city?.includes(VICINITY);
  const countryStr = location.country ? location.country.replace(` ${VICINITY}`, '') : '';
  const stateStr = location.state ? location.state : '';
  const cityStr = location.city ? `${location.city}${(countryIncludesVicinity && !cityIncludesVicinity) ? ` ${VICINITY}` : ''}` : '';
  return `${cityStr}${location.state && !isEmpty(cityStr) ? ', ' : ''}${stateStr}${location.country ? ', ' : ''}${countryStr}`;
};

//Quitar el default check si no tienen datos
export const filterDefaultCheckedValues = (
  defaultCheckedValues: string[],
  condosStore: IGateway | null,
  premiereStore: IGateway | null,
  valueStore: IGateway | null,
  staycationStore: IGateway,
  cruiseStore: IGateway,
  fantasyStore: IGateway,
) => {
  return defaultCheckedValues.filter((value) => {
    switch (value) {
      case GetawaysLocationEnum.NewCondos:
        return condosStore?.getawaysTotalCount !== 0;
      case GetawaysCondoSubCategoryEnum.PremierEscapes:
        return premiereStore?.getawaysTotalCount !== 0;
      case GetawaysCondoSubCategoryEnum.ValueGetaways:
        return valueStore?.getawaysTotalCount !== 0;
      case GetawaysLocationEnum.Staycation:
        return staycationStore.getawaysTotalCount !== 0;
      case GetawaysLocationEnum.Cruise:
        return cruiseStore.getawaysTotalCount !== 0;
      case GetawaysLocationEnum.Fantasy:
        return fantasyStore.getawaysTotalCount !== 0;
      default:
        return true;
    }
  });
};

export const getSortOptions = (
  hasQuick: boolean,
  isB2C: boolean,
  walletWalletSavings: boolean,
  displayRecommended: boolean,
  displayRecommendedCustomerAgent: boolean,
  hasSavedProperties?: boolean,
): LabeledValue[] => {
  const sorts = [];

  if (displayRecommended) {
    sorts.push({ value: SortTypes.Recommended, label: <FormattedMessage id="sort.recommended" /> });
  }
  if (displayRecommendedCustomerAgent) {
    sorts.push({ value: SortTypes.RecommendedCustomerAgent, label: <FormattedMessage id="sort.biggest.percent" /> });
  }
  
  if (hasQuick) {
    sorts.push({ value: SortTypes.MostBooked, label: <FormattedMessage id="sort.most.booked" /> });
  }

  if ((!isB2C || walletWalletSavings) && !displayRecommendedCustomerAgent) {
    sorts.push({
      value: SortTypes.BiggestSavingsPercentage,
      label: <FormattedMessage id="sort.biggest.percent" />,
    });
    sorts.push({ value: SortTypes.BiggestSavings, label: <FormattedMessage id="sort.biggest" /> });
  }

  sorts.push(
    { value: SortTypes.GuestRating, label: <FormattedMessage id="sort.guest" /> },
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.lowest.price" /> },
    { value: SortTypes.PriceDesc, label: <FormattedMessage id="sort.highest.price" /> },
    { value: SortTypes.DistanceAsc, label: <FormattedMessage id="sort.nearest" /> },
  );

  if (hasSavedProperties) {
    sorts.push({ value: SortTypes.SavedProperties, label: <FormattedMessage id="sort.saved.properties" /> });
  }

  return sorts;
};

export const getCarsSortOptions = (
  isB2C: boolean | undefined,
  walletWalletSavings: boolean | undefined,
): LabeledValue[] => {
  const sorts = [];
  if (!isB2C || walletWalletSavings) {
    sorts.push({
      value: SortTypes.BiggestSavingsPercentage,
      label: <FormattedMessage id="sort.biggest.percent" />,
    });
    sorts.push({ value: SortTypes.BiggestSavings, label: <FormattedMessage id="sort.biggest" /> });
  }

  sorts.push(
    { value: SortTypes.BrandNames, label: <FormattedMessage id="sort.brand.names" /> },
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.lowest.price" /> },
    { value: SortTypes.PriceDesc, label: <FormattedMessage id="sort.highest.price" /> },
  );
  return sorts;
};

export const GetLifeStyleStores = () => {
  return [
    { value: VALUE_LIFESTYLE, label: VALUE_LIFESTYLE_DESC },
    { value: CHOICE_LIFESTYLE, label: CHOICE_LIFESTYLE_DESC },
    { value: UNLIMITED_LIFESTYLE, label: UNLIMITED_LIFESTYLE_DESC },
  ];
};

export const getCondoSortOptions = (
  condoState: ICondosState,
  isB2C: boolean | undefined,
  walletWalletSavings: boolean | undefined,
  hasSavedProperties?: boolean
): LabeledValue[] => {
  const { cachedSelectedLocation, lastRequestType } = condoState;
  const isSpecificCondo: boolean =
    cachedSelectedLocation?.locationType === CondoLocationsEnum.GenericCondo;
  const isAnytime: boolean = lastRequestType === CondoRequestTypeEnum.Anytime;

  const options = [];

  if (!isB2C || walletWalletSavings) {
    options.push({
      value: SortTypes.BiggestSavingsPercentage,
      label: <FormattedMessage id="sort.biggest.percent" />,
    });
    options.push({
      value: SortTypes.BiggestSavings,
      label: <FormattedMessage id="sort.biggest" />,
    });
  }

  options.push(
    { value: SortTypes.MostPopular, label: <FormattedMessage id="most.popular" /> },
    { value: SortTypes.PriceAsc, label: <FormattedMessage id="sort.lowest.price" /> },
    { value: SortTypes.PriceDesc, label: <FormattedMessage id="sort.highest.price" /> },
  );

  if (isSpecificCondo) {
    options.push({ value: SortTypes.Nearest, label: <FormattedMessage id="sort.nearest" /> });
  }

  if (!isAnytime) {
    options.push({
      value: SortTypes.ClosestDateRange,
      label: <FormattedMessage id="closest.date.range" />,
    });
  }

  if (lastRequestType === CondoRequestTypeEnum.Flexible) {
    options.push({
      value: SortTypes.FarthestDateRange,
      label: <FormattedMessage id="farthest.date.range" />,
    });
  }

  if (isSpecificCondo && isAnytime) {
    return [
      { value: SortTypes.MostPopular, label: <FormattedMessage id="most.popular" /> },
      { value: SortTypes.Nearest, label: <FormattedMessage id="sort.nearest" /> },
    ];
  }

  if (hasSavedProperties) {
    options.push({ value: SortTypes.SavedProperties, label: <FormattedMessage id="sort.saved.properties" /> });
  }

  return options;
};








const handleFilterChangeArray = (isCars: boolean | null | undefined, dispatch: Dispatch<any>, key: string, value: any[]) => {
  if (isCars) {
    if (key === 'budget') {
      dispatch(carsFiltersActions.setBudget(value));
    } else if (key === 'airCondition') {
      dispatch(carsFiltersActions.setAirCondition(value));
    } else if (key === 'carType') {
      dispatch(carsFiltersActions.setCarType(value));
    } else if (key === 'simpleType') {
      dispatch(carsFiltersActions.setSimpleType(value));
    } else if (key === 'category') {
      dispatch(carsFiltersActions.setCategory(value));
    } else if (key === 'doors') {
      dispatch(carsFiltersActions.setDoors(value));
    } else if (key === 'driveType') {
      dispatch(carsFiltersActions.setDriveType(value));
    } else if (key === 'fuel') {
      dispatch(carsFiltersActions.setFuel(value));
    } else if (key === 'rateDistance') {
      dispatch(carsFiltersActions.setRateDistance(value));
    } else if (key === 'rentalCompany') {
      dispatch(carsFiltersActions.setRentalCompany(value));
    } else if (key === 'seats') {
      dispatch(carsFiltersActions.setSeats(value));
    } else if (key === 'transmissionType') {
      dispatch(carsFiltersActions.setTransmissionType(value));
    } else if (key === 'refundable') {
      dispatch(carsFiltersActions.setRefundable(value));
    } else if (key === 'whereLocation') {
      dispatch(carsFiltersActions.setWhereLocation(value));    
    }  
  } else {
    if (key === 'budget') {
      dispatch(filtersActions.setBudget(value));
    } else if (key === 'amenities') {
      dispatch(filtersActions.setAmenities(value));
    } else if (key === 'accommodationType') {
      dispatch(filtersActions.setAccommodationType(value));
    } else if (key === 'accessibility') {
      dispatch(filtersActions.setAccessibility(value));
    } else if (key === 'popularLocations') {
      dispatch(filtersActions.setPopularLocations(value));
    } else if (key === 'boardNames') {
      dispatch(filtersActions.setBoardName(value));
    } else if (key === 'refundable') {
      dispatch(filtersActions.setRefundable(value));
    } else if (key === 'flexibleDate') {
      dispatch(filtersActions.setFlexibleDate(value));
    } else if (key === 'neighbourhoods') {
      dispatch(filtersActions.setNeighbourhoods(value));
    } else if (key === 'places') {
      dispatch(filtersActions.setPlaces(value));
    } else if (key === 'starRatings') {
      dispatch(filtersActions.setStarRating(value));
    }
  }
};

const handleFilterChangeString = (dispatch: Dispatch<any>, key: string, value: string) => {
  if (key === 'propertyName') {
    dispatch(filtersActions.setPropertyName(value));
  } else if (key === 'searchHomes') {
    dispatch(filtersActions.setSearchHomes(value as HomesTypes));
  }
};

export const handleFilterRemove = (isCars: boolean | null | undefined, filtersStore: any, dispatch: Dispatch<any>, key: string, value: any, isArray: boolean) => {
  if (isArray) {
    const values = filtersStore[key as keyof IFiltersState]?.filter((v: any) => v?.toString() !== value?.toString());
    handleFilterChangeArray(isCars, dispatch, key, values);
  } else {
    handleFilterChangeString(dispatch, key, value);
  }
};

export const EXCLUDED_FILTERS_KEYS = ['autocompleteItems', 'autocompleteLoading', 'selectedAutocompleteItem', 'selectedAutocompleteLabel', 'searchHomes', 'distance', 'propertyName', 'sortBy'];

export const getFilterValues = (filtersStore: any, extraExcludedKeys: string[] = [], isCars: boolean | null | undefined) => {
  const excludedKeys = [...EXCLUDED_FILTERS_KEYS, ...extraExcludedKeys];
  const filterKeys = Object.keys(filtersStore)?.filter((key) => !excludedKeys.includes(key));
  const filterKeysWithValues = filterKeys.filter((key) => 
    isCars ? 
        Array.isArray(filtersStore[key as keyof ICarsFiltersState]) && filtersStore[key as keyof ICarsFiltersState].length > 0 :
        Array.isArray(filtersStore[key as keyof IFiltersState]) && filtersStore[key as keyof IFiltersState].length > 0);
  const values = filterKeysWithValues.map(f => (isCars ? 
                                            filtersStore[f as keyof ICarsFiltersState] : 
                                            filtersStore[f as keyof IFiltersState])?.map((v: any) => ({
    key: f,
    value: v,
    isArray: true
  }))).flat();
  return values;
};